import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

export const COUNTRY_CODE_OPTIONS = [
  { code: "+91", flag: "🇮🇳", name: "India" },
  { code: "+1", flag: "🇺🇸", name: "USA / Canada" },
  { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "+61", flag: "🇦🇺", name: "Australia" },
  { code: "+65", flag: "🇸🇬", name: "Singapore" },
  { code: "+971", flag: "🇦🇪", name: "UAE" },
  { code: "+60", flag: "🇲🇾", name: "Malaysia" },
  { code: "+62", flag: "🇮🇩", name: "Indonesia" },
  { code: "+66", flag: "🇹🇭", name: "Thailand" },
  { code: "+63", flag: "🇵🇭", name: "Philippines" },
  { code: "+92", flag: "🇵🇰", name: "Pakistan" },
  { code: "+880", flag: "🇧🇩", name: "Bangladesh" },
  { code: "+94", flag: "🇱🇰", name: "Sri Lanka" },
  { code: "+977", flag: "🇳🇵", name: "Nepal" },
] as const;

export function CountryCodePicker({
  value,
  onChange,
  className,
  align = "auto",
}: {
  value: string;
  onChange: (countryCode: string) => void;
  className?: string;
  align?: "left" | "right" | "auto";
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const current = COUNTRY_CODE_OPTIONS.find((option) => option.code === value) ?? COUNTRY_CODE_OPTIONS[0];

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxHeight = 280;
    const below = window.innerHeight - rect.bottom;
    const opensUp = below < maxHeight + 12 && rect.top > below;
    const menuWidth = Math.max(rect.width, 220);

    let left = rect.left;
    if (align === "right" || (align === "auto" && left + menuWidth > window.innerWidth - 12)) {
      left = rect.right - menuWidth;
    }
    left = Math.max(12, Math.min(left, window.innerWidth - menuWidth - 12));

    setMenuStyle({
      position: "fixed",
      left,
      width: menuWidth,
      ...(opensUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxHeight, (opensUp ? rect.top : below) - 12),
      zIndex: 9999,
    });
  }, [align]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!buttonRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Select country code"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        className={cn(
          "inline-flex min-w-24 shrink-0 items-center gap-1.5 rounded-lg border border-line bg-canvas px-2.5 py-2 font-mono text-sm text-ink transition-colors hover:border-brand/40 focus:border-brand focus:outline-none",
          className,
        )}
      >
        <span className="text-lg leading-none">{current.flag}</span>
        <span className="font-semibold">{current.code}</span>
        <ChevronDown className={cn("ml-auto h-3.5 w-3.5 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Country codes"
          style={menuStyle}
          className="flex flex-col overflow-hidden rounded-xl border border-line bg-elevated shadow-xl"
        >
          <ul className="flex-1 overflow-y-auto py-1">
            {COUNTRY_CODE_OPTIONS.map((option) => (
              <li key={option.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.code === value}
                  onClick={() => {
                    onChange(option.code);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-soft",
                    option.code === value ? "bg-brand/10 dark:bg-brand/20 font-semibold text-brand" : "text-ink-2",
                  )}
                >
                  <span className="shrink-0 text-xl leading-none">{option.flag}</span>
                  <span className="w-10 shrink-0 font-mono text-xs font-semibold">{option.code}</span>
                  <span className="truncate text-xs text-ink-3">{option.name}</span>
                  {option.code === value && <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}

